import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { VStack } from '@televet/kibble-ui/build/chakra';
import { useExportOrDownloadChannel } from 'pages/Conversations/hooks/useExportOrDownloadChannel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { getClinicPetParentDisplayName } from 'shared/utils';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { resetExportData, updateExportData } from 'pages/Conversations/state/conversationsSlice';
import usePersistedState from 'shared/hooks/usePersistedState';
import { PersistedStateKeys } from 'shared/enums';
import format from 'date-fns/format';

export enum FileNameErrorMessage {
  INVALID_CHARS = 'Your file name may only contain letters, number, spaces and any of the following special characters: _ - . :',
  MIN_CHARS = 'Please specify a file name.',
  MAX_CHARS = 'Your file name must be less than 150 characters long',
}

const MAX_CHARS = 150;

const ExportOrDownloadChannelModal = (): JSX.Element => {
  const { primaryIntegrationName } = useIntegrationsProvider();
  const dispatch = useAppDispatch();
  const [fileNameErrorMessage, setFileNameErrorMessage] = useState<FileNameErrorMessage | null>(null);
  const [shouldIncludeNotes, setShouldIncludeNotes] = usePersistedState(
    PersistedStateKeys.ShouldIncludeNotesInConversationExport,
    false,
  );
  const { isExportModalOpen, channelId, selectedExportType, exportFileName, channelPetParents } = useAppSelector(
    (state) => state.conversations.exportData,
  );
  const { exportChannelToPims, downloadChannelPdf, isLoading } = useExportOrDownloadChannel({
    channelId,
    shouldIncludeNotes,
  });

  const closeModal = useCallback(() => {
    dispatch(resetExportData());
  }, [dispatch]);

  const defaultFileName = useMemo(() => {
    const petParentNames = channelPetParents.map((petParent) => getClinicPetParentDisplayName(petParent)).join('_');
    const currentDate = format(new Date(), 'MMM d yyyy hh:mm a');
    const remainingChars = MAX_CHARS - currentDate.length;
    const truncatedPetParentNames = petParentNames.slice(0, remainingChars);

    return `${truncatedPetParentNames}_${currentDate}`.replaceAll(' ', '-');
  }, [channelPetParents]);

  const handleSubmit = useCallback(() => {
    switch (selectedExportType) {
      case 'ExportToPims':
        exportChannelToPims();
        break;
      case 'DownloadPdf':
        downloadChannelPdf();
        break;
      default:
        break;
    }
  }, [selectedExportType, exportChannelToPims, downloadChannelPdf]);

  const handleFileNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value.length > MAX_CHARS) {
        return;
      }
      dispatch(updateExportData({ exportFileName: event.target.value }));
    },
    [dispatch],
  );

  const handleToggleCheckbox = useCallback(
    () => setShouldIncludeNotes((prevState) => !prevState),
    [setShouldIncludeNotes],
  );

  const cannotEditFileName = useMemo(
    () => primaryIntegrationName === 'Neo' && selectedExportType === 'ExportToPims',
    [primaryIntegrationName, selectedExportType],
  );

  const validateFileName = (): void => {
    const containsInvalidCharacters = exportFileName.match(/[^a-zA-Z0-9\_\-\:\.\ ]/gi);

    switch (true) {
      case !!containsInvalidCharacters?.length:
        setFileNameErrorMessage(FileNameErrorMessage.INVALID_CHARS);
        break;
      case exportFileName.length <= 0:
        setFileNameErrorMessage(FileNameErrorMessage.MIN_CHARS);
        break;
      case exportFileName.length > MAX_CHARS:
        setFileNameErrorMessage(FileNameErrorMessage.MAX_CHARS);
        break;
      default:
        setFileNameErrorMessage(null);
        break;
    }
  };

  useEffect(() => {
    // Set default file name
    dispatch(updateExportData({ exportFileName: defaultFileName }));
  }, [defaultFileName, dispatch]);

  return (
    <Modal useFullScreenMobile={false} size="md" onClose={closeModal} isOpen={isExportModalOpen}>
      <ModalCloseButton />
      <ModalHeader>
        {selectedExportType === 'ExportToPims' ? `Export to ${primaryIntegrationName}` : `Download as PDF`}
      </ModalHeader>
      <ModalBody>
        <VStack spacing={4} align="flex-start">
          <TextInput
            placeholder="File name goes here"
            value={exportFileName}
            onChange={handleFileNameChange}
            onBlur={validateFileName}
            label="File Name"
            isInvalid={!!fileNameErrorMessage}
            errorText={fileNameErrorMessage || ''}
            isDisabled={cannotEditFileName}
            cursor={cannotEditFileName ? 'not-allowed' : 'pointer'}
            charLimit={MAX_CHARS}
          />
          <Checkbox isChecked={shouldIncludeNotes} onChange={handleToggleCheckbox}>
            Include notes
          </Checkbox>
        </VStack>
      </ModalBody>
      <ModalFooterButtons
        isLoading={isLoading}
        onSubmit={handleSubmit}
        isDisabled={!!fileNameErrorMessage}
        data-testid="export-channel-modal-submit-button"
        submitText={selectedExportType === 'ExportToPims' ? 'Export' : 'Download'}
      />
    </Modal>
  );
};

export default ExportOrDownloadChannelModal;
